'use client';
import React, { useEffect, useState } from 'react';
import { IconHome, IconMenu2, IconX, IconLogout } from '@tabler/icons-react';
import { ColorSchemeToggle } from '../ColorSchemeToggle';
import { Group, ActionIcon, Menu, Text, Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useRouter, usePathname } from 'next/navigation';
import auth from '@/utils/auth';
import useSafeTranslation from '@/hooks/useSafeTranslation';
import ConnectivityIndicator from '../ui/ConnectivityIndicator';

export type HeaderProps = {
  onOpenMobileMenu?: () => void;
  sidebarVisible?: boolean;
  // optional server-provided auth state used during SSR to avoid reading
  // localStorage on the server and causing hydration mismatches.
  initialServerAuth?: boolean;
};

export function Header({ onOpenMobileMenu, sidebarVisible, initialServerAuth }: HeaderProps) {
  const { i18n, tSafe: t } = useSafeTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const [logged, setLogged] = useState<boolean>(
    typeof window !== 'undefined' ? auth.isAuthenticated() : !!initialServerAuth
  );

  useEffect(() => {
    // refresh local state when pathname changes (simple reactive check)
    setLogged(typeof window !== 'undefined' ? auth.isAuthenticated() : !!initialServerAuth);
    // No debug logs in production
  }, [pathname, initialServerAuth]);

  return (
    <header className="app-header">
      <div className="header-left">
        <Group gap="xs" align="center">
          {/* Mobile menu button - visible only on small viewports via CSS */}
          <ActionIcon
            className="mobile-menu-button"
            aria-label={sidebarVisible ? t('ocultar') : t('mostrarTodos')}
            aria-expanded={!!sidebarVisible}
            variant="subtle"
            onClick={() => onOpenMobileMenu && onOpenMobileMenu()}
          >
            {sidebarVisible ? <IconX size={18} /> : <IconMenu2 size={18} />}
          </ActionIcon>
          {/*Collapse toggle moved to Sidebar component*/}
          <div className="brand">
            <IconHome size={20} />
            <span className="brand-title">{t('appTitle')}</span>
            {/* mobile-only compact title to save horizontal space */}
            <span className="brand-title-mobile">{t('appTitleMobile')}</span>
          </div>
        </Group>
      </div>

      <div className="header-right">
        <Group gap="xs">
          <ConnectivityIndicator />
          <ColorSchemeToggle />

          {/* Language indicator + quick menu */}
          <Menu withinPortal position="bottom-end" shadow="md">
            <Menu.Target>
              <ActionIcon
                aria-label={t('selectLanguageAria') ?? 'Seleccionar idioma'}
                variant="light"
              >
                <Text size="md" fw={700} lh={1}>
                  {i18n.language?.toUpperCase() ?? 'ES'}
                </Text>
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => i18n.changeLanguage('es')}>Español (es)</Menu.Item>
              <Menu.Item onClick={() => i18n.changeLanguage('en')}>English (en)</Menu.Item>
              <Menu.Item onClick={() => i18n.changeLanguage('bg')}>Български (bg)</Menu.Item>
              <Menu.Item onClick={() => i18n.changeLanguage('ro')}>Română (ro)</Menu.Item>
              <Menu.Divider />
              {!logged && (
                <Menu.Item onClick={() => router.push('/login')}>{t('login.button')}</Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>

          {/* Move logout button to the far right for quick access */}
          {logged && (
            <Button
              className="logout-btn"
              variant="outline"
              color="red"
              onClick={() =>
                modals.openConfirmModal({
                  title: t('logout.title'),
                  children: t('logout.confirmQuestion'),
                  labels: { confirm: t('logout.confirm'), cancel: t('cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => {
                    auth.logout();
                    setLogged(false);
                    router.push('/login');
                  },
                })
              }
            >
              <Group gap="xs" align="center" wrap="nowrap">
                <IconLogout size={16} />
                {t('logout.button')}
              </Group>
            </Button>
          )}
          {/* Icon-only logout for small screens */}
          {logged && (
            <ActionIcon
              className="logout-icon"
              aria-label={t('logout.title')}
              variant="light"
              color="red"
              onClick={() =>
                modals.openConfirmModal({
                  title: t('logout.title'),
                  children: t('logout.confirmQuestion'),
                  labels: { confirm: t('logout.confirm'), cancel: t('cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => {
                    auth.logout();
                    setLogged(false);
                    router.push('/login');
                  },
                })
              }
            >
              <IconLogout size={18} />
            </ActionIcon>
          )}
        </Group>
      </div>
    </header>
  );
}
